import type { IBooleanSearchChipItem } from "@similarweb/ui-components";
import { IconButton } from "@similarweb/ui-components";
import { swSettings } from "app/common/services/swSettings";
import type { RootState } from "single-spa/store/types";
import {
    CellKeywordPhrase,
    GroupTrafficShare,
    IndexCell,
    OrganicPaid,
    TrafficShareWithVisits,
} from "app/components/React/Table/cells";
import { DefaultCellHeader, HeaderCellBlank } from "app/components/React/Table/headerCells";
import SWReactTableWrapper from "app/components/React/Table/SWReactTableWrapper";
import { i18nFilter } from "app/filters/i18nFilter";
import { numberFilter } from "app/filters/ngFilters";
import {
    booleanSearchToObject,
    booleanSearchToString,
    booleanSearchUrlToComponent,
} from "app/pages/website-analysis/traffic-sources/search/booleanSearchUtility";
import { PhrasesEnrichedCompare } from "app/pages/website-analysis/traffic-sources/search/tabs/PhrasesEnrichedCompare";
import { PhrasesEnrichedSingle } from "app/pages/website-analysis/traffic-sources/search/tabs/PhrasesEnrichedSingle";
import { PhrasesTableTop } from "app/pages/website-analysis/traffic-sources/search/tabs/PhrasesTableTop";
import queryString from "query-string";
import type { FunctionComponent } from "react";
import { useState } from "react";
import { KeywordAdvancedFilterService } from "app/services/AdvancedFilterService/KeywordsAdvancedFilters";
import DurationService from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";
import UIComponentStateService from "app/services/UIComponentStateService";
import { useSelector } from "react-redux";
import { useCurrentStateAndParams } from "@uirouter/react";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { chosenSites } from "app/common/services/chosenSites";
import { useDispatch } from "react-redux";
import { TOGGLE_ON_COMPARE_DROPDOWN } from "app/action_types/website_analysis_action-types";

const LOCALE_STORAGE_KEY = "keyword-phrases-single";
export const Phrases: FunctionComponent<any> = () => {
    const { params } = useCurrentStateAndParams();
    const chosenItems = useSelector((state: RootState) => {
        const {
            routing: { chosenItems },
        } = state;
        return chosenItems;
    });
    const dispatch = useDispatch();
    const i18n = i18nFilter();
    const isCompare = chosenSites.isCompare();
    const firstWordsCountItem = { value: "noFilter", label: i18n("forms.search.anylength") };
    const pageSize = 100;
    const [wordsCountItems, setWordsCountItems] = useState([firstWordsCountItem]);
    const [pageNumber, setPageNumber] = useState(0);
    const changePageCallback = (page) => setPageNumber(page);
    const {
        key,
        country,
        isWWW,
        duration,
        webSource,
        BooleanSearchTerms,
        groupedKeywords_orderby,
        groupedKeywords_filters,
        groupedKeywords_page,
        limits,
        IncludeBranded,
        IncludeNoneBranded,
    } = params;
    const filtersObject: any = {
        searchTerm: {
            action: "contains",
        },
        wordCount: {
            action: "==",
            type: "number",
        },
    };
    const selectedAdvancedFilter: any = KeywordAdvancedFilterService.getFilterById(limits);
    // Parse filters from URL string to object
    const availableFilters: any = {};
    if (groupedKeywords_filters) {
        groupedKeywords_filters.split("+").forEach((filter) => {
            const values = filter.split(";");
            availableFilters[values[0]] = { ...filtersObject[values[0]], value: values[2] };
        });
    }
    // Convert filters to API params filter string.
    const filter = Object.keys(availableFilters)
        .map((filter) => {
            const { action, value, type } = availableFilters[filter];
            let filterProperty = filter;
            // ¯\_(ツ)_/¯, copying from the SearchCtrl.
            if (filterProperty === "excludeBranded") {
                filterProperty = "searchTerm";
            }
            return [filterProperty, action, type === "number" ? value : `"${value}"`].join(";");
        })
        .join(",");
    // to set the button on the selected branded filter
    let selectedBrandedFilter = "";
    if (IncludeBranded === "true") {
        selectedBrandedFilter = "branded";
    } else if (IncludeNoneBranded === "true") {
        selectedBrandedFilter = "notBranded";
    } else {
        selectedBrandedFilter = "";
    }
    const createFiltersForUrl = (filters) =>
        Object.keys(filters)
            .map((filter) => {
                const { action, value } = filters[filter];
                return [filter, action, value].join(";");
            })
            .join("+");

    let field = "Share";
    let sortDirection = "desc";
    if (groupedKeywords_orderby) {
        [field, sortDirection] = groupedKeywords_orderby.split(" ");
    }
    const decodedBooleanSearchTerms = decodeURIComponent(BooleanSearchTerms);
    const { IncludeTerms, ExcludeTerms } = booleanSearchToObject(decodedBooleanSearchTerms);
    const durationObject = DurationService.getDurationData(duration);
    const { isCustom } = durationObject.raw;
    const { from, to, isWindow } = durationObject.forAPI;

    const initialFilters = {
        filter,
        country,
        from,
        isWWW: isWWW !== "*",
        isWindow,
        key,
        orderby: `${field} ${sortDirection}`,
        sort: field,
        asc: sortDirection === "asc",
        page: groupedKeywords_page,
        to,
        webSource,
        limits: selectedAdvancedFilter.api,
        ...(IncludeTerms && { IncludeTerms }),
        ...(ExcludeTerms && { ExcludeTerms }),
        IncludeBranded,
        IncludeNoneBranded,
    } as any;
    // @ts-ignore FIXME: getDiffSymbol expects Dayjs but forAPI.from/to are strings — callers should use raw (Dayjs) dates instead
    const durationDiff = DurationService.getDiffSymbol(from, to);
    const showTrafficOverTimeChartNoData = ["28d", "1m"].includes(durationDiff);
    const isMobileWeb = webSource === "MobileWeb";
    const columns = [
        {
            fixed: true,
            cellComponent: ({ row }) => {
                return (
                    <>
                        {!row.parent && (
                            <IconButton
                                iconName="chev-right"
                                type="flat"
                                className="enrich"
                            />
                        )}
                    </>
                );
            },
            sortable: false,
            headerComponent: HeaderCellBlank,
            isResizable: false,
            width: 48,
            columnClass: "collapseControlColumn", // optional
            cellClass: "collapseControlCell", // optional
            disableHeaderCellHover: true,
        },
        {
            fixed: true,
            cellComponent: IndexCell,
            disableHeaderCellHover: true,
            sortable: false,
            width: 46,
            // cellClass: 'kwa-ads-index',
            isResizable: false,
        },
        {
            field: "SearchTerm",
            displayName: i18n("analysis.source.search.grouped.table.columns.searchterms.title"),
            cellComponent: CellKeywordPhrase,
            sortable: true,
            showTotalCount: true,
            minWidth: isCompare ? 295 : 450,
        },
        {
            field: "Share",
            displayName: i18n(
                isCompare
                    ? "analysis.source.search.all.table.columns.totalShareCompare.title"
                    : "analysis.source.search.all.table.columns.share.title",
            ),
            tooltip: i18n(
                isCompare
                    ? "analysis.source.search.all.table.columns.totalShareCompare.title.tooltip"
                    : "analysis.source.search.all.table.columns.share.title.tooltip",
            ),
            cellComponent: TrafficShareWithVisits,
            sortable: true,
            width: isCompare ? 162 : 200,
        },
        ...(isCompare
            ? [
                  {
                      field: "SiteOrigins",
                      displayName: i18n("analysis.source.search.all.table.columns.shareCompare.title"),
                      cellComponent: GroupTrafficShare,
                      sortable: false,
                      width: 202,
                  },
              ]
            : []),
        {
            field: "Organic",
            displayName: i18n("analysis.source.search.all.table.columns.Org/Paid.title"),
            tooltip: i18n("phrases.table.columns.vs.title.tooltip"),
            cellComponent: OrganicPaid,
            width: 170,
            lastColumnFixed: true,
        },
    ].map((col: any) => {
        const isSorted = col.field === field;
        return {
            ...col,
            visible: col.visible !== false,
            headerComponent: col.headerComponent || DefaultCellHeader,
            isSorted,
            sortDirection,
            isResizable: col.isResizable !== false,
        };
    });
    const onSort = ({ field, sortDirection }) => {
        proNavigator.applyUpdateParams({
            groupedKeywords_orderby: `${field} ${sortDirection}`,
        });
    };
    const onBrandedFilterChange = (nextBrandedFilter) => {
        proNavigator.applyUpdateParams({
            groupedKeywords_filters: createFiltersForUrl(availableFilters),
            IncludeBranded: nextBrandedFilter.value === "nonBranded",
            IncludeNoneBranded: nextBrandedFilter.value === "branded",
        });
    };
    const onWordsCountFilterChange = (nextWordsFilter) => {
        let filters;
        if (nextWordsFilter === -1) {
            const { wordCount, ...restFilters } = availableFilters;
            filters = restFilters;
        } else {
            const wordCount = { ...filtersObject.wordCount };
            wordCount.value = nextWordsFilter.value;
            filters = { ...availableFilters, wordCount };
        }
        proNavigator.applyUpdateParams({
            groupedKeywords_filters: createFiltersForUrl(filters),
        });
    };
    const onSearchTermFilterChange = (chipsObject: IBooleanSearchChipItem[]) => {
        const BooleanSearchTerms = booleanSearchToString(chipsObject);
        proNavigator.applyUpdateParams({
            BooleanSearchTerms,
        });
    };
    const getDataCallback = (data) => {
        const {
            Header: { WordCount },
        } = data;
        setWordsCountItems([
            firstWordsCountItem,
            ...Object.keys(WordCount).map((numOfWords) => {
                return {
                    value: numOfWords,
                    label: `${numOfWords} words (${numberFilter()(WordCount[numOfWords])})`,
                };
            }),
        ]);
    };
    const onClearAll = () => {
        proNavigator.applyUpdateParams({
            groupedKeywords_filters: null,
            limits: null,
            BooleanSearchTerms: null,
            IncludeBranded: null,
            IncludeNoneBranded: null,
        });
    };
    const downloadExcelPermitted = swSettings.current.resources.IsExcelAllowed;
    const getExcelUrl = () => {
        const queryStringParams = queryString.stringify(initialFilters);
        return `/api/keywordphrases/${
            isMobileWeb ? "GetTrafficSourcesGroupedKeywordsTsvMobileWeb" : "GetTrafficSourcesGroupedKeywordsTsv"
        }?${queryStringParams}`;
    };
    const onSingleCompetitorsClick = () => {
        if (chosenItems.length === 1) {
            allTrackers.trackEvent("drop down", "open", "Keyword analysis advanced filter");
            dispatch({ type: TOGGLE_ON_COMPARE_DROPDOWN });
        }
    };
    const onCompareCompetitorsClick = (item) => {
        let filters;
        if (item && item.id === "opportunities") {
            filters = { ...availableFilters };
        }
        if (UIComponentStateService.getItem(LOCALE_STORAGE_KEY, "localStorage", false) === "true") {
            UIComponentStateService.setItem(LOCALE_STORAGE_KEY, "localStorage", null, false);
        }
        proNavigator.applyUpdateParams({
            limits: item.id,
            ...(filters && {
                groupedKeywords_filters: createFiltersForUrl(filters),
                IncludeBranded: false,
                IncludeNoneBranded: true,
            }),
        });
    };
    const onCloseCompetitiveFilter = () => {
        proNavigator.applyUpdateParams({
            limits: null,
        });
    };
    const transformData = (data) => {
        return {
            ...data,
            Data: data.Data.map((row) => {
                return {
                    ...row,
                    SharePerMonth: row.SharePerMonth ? row.SharePerMonth : [],
                    SiteOriginsPerMonth: row.SiteOriginsPerMonth ? row.SiteOriginsPerMonth : [],
                    TotalVisits: row.Visits,
                };
            }),
        };
    };
    return (
        <SWReactTableWrapper
            transformData={transformData}
            changePageCallback={changePageCallback}
            tableOptions={{
                metric: "TrafficSourcesGroupedKeywordsTable",
                get EnrichedRowComponent() {
                    return (props) =>
                        isCompare ? (
                            <PhrasesEnrichedCompare
                                {...props}
                                selectedSites={key}
                                pageSize={pageSize}
                                pageNumber={pageNumber}
                                showTrafficOverTimeChartNoData={showTrafficOverTimeChartNoData}
                            />
                        ) : (
                            <PhrasesEnrichedSingle
                                {...props}
                                pageSize={pageSize}
                                pageNumber={pageNumber}
                                showTrafficOverTimeChartNoData={showTrafficOverTimeChartNoData}
                            />
                        );
                },
                get enrichedRowComponentAppendTo() {
                    return ".keyword-phrases-table";
                },
                get enrichedRowComponentHeight() {
                    return 580;
                },
                shouldApplyEnrichedRowHeightToCell: false,
                shouldEnrichRow: (props, index, e) => {
                    const openEnrich = e?.currentTarget?.childNodes?.[0]?.className.includes("enrich");
                    if (openEnrich) {
                        allTrackers.trackEvent("Open", "Click", "Phrases Over Time/Expand");
                    }

                    return props.tableData[index].SearchTerm !== "grid.upgrade" && openEnrich;
                },
                get enrichOnLoadRowNumber() {
                    let isMinThreeMonths;
                    // 'isCustom' ie. not a preset duration.
                    if (!isCustom) {
                        isMinThreeMonths = duration !== "28d" && duration !== "1m";
                    } else {
                        isMinThreeMonths = DurationService.diffByUnit(from, to, "months") + 1 >= 3;
                    }
                    if (isMinThreeMonths) {
                        return 1;
                    }
                    return null;
                },

                onEnrichedRowClick: (isOpen, rowIndex, row) => {
                    // console.log('onEnrichedRowClick');
                },
                customTableClass: "keyword-phrases-table",
            }}
            serverApi={`/api/keywordphrases/${
                isMobileWeb ? "GetTrafficSourcesGroupedKeywordsTableMobileWeb" : "GetTrafficSourcesGroupedKeywordsTable"
            }`}
            tableColumns={columns}
            initialFilters={initialFilters}
            recordsField="Data"
            totalRecordsField="TotalCount"
            onSort={onSort}
            preventDefaultOnSort
            getDataCallback={getDataCallback}
            pageIndent={1}
        >
            {(topComponentProps) => (
                <PhrasesTableTop
                    {...topComponentProps}
                    onBrandedFilterChange={onBrandedFilterChange}
                    selectedBrandedFilter={selectedBrandedFilter}
                    wordsCountItems={wordsCountItems}
                    selectedWordsCountFilter={availableFilters.wordCount && availableFilters.wordCount.value}
                    onWordsCountFilterChange={onWordsCountFilterChange}
                    onClearAll={onClearAll}
                    searchValue={BooleanSearchTerms && booleanSearchUrlToComponent(decodedBooleanSearchTerms)}
                    onChange={onSearchTermFilterChange}
                    downloadExcelPermitted={downloadExcelPermitted}
                    excelLink={getExcelUrl()}
                    isCompare={isCompare}
                    chosenItems={chosenItems}
                    onSingleCompetitorsClick={onSingleCompetitorsClick}
                    onCompareCompetitorsClick={onCompareCompetitorsClick}
                    selectedCompetitiveFilter={selectedAdvancedFilter}
                    onCloseCompetitiveFilter={onCloseCompetitiveFilter}
                    openCompetitiveFilter={
                        UIComponentStateService.getItem(LOCALE_STORAGE_KEY, "localStorage", false) === "true"
                    }
                />
            )}
        </SWReactTableWrapper>
    );
};
